import { KeyRound, Lock, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFixedParam } from '@/lib/scopedParams';
import { api } from '../../api';
import type { EppCertificateView, EppConnection, EppCredentialView } from '../../api/areas/registry';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { daysUntil, formatDate, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ClassTable, CREDENTIAL_STATUS, EnumBadge, Flag, ms, RefreshButton, RegistryRef, RegistrySelect, ResetButton, SubHeading, useParamSetter, useRegistryCrumbs } from './shared';

/** A secret is shown only as "configured"; the reference itself only to holders of registry.credential.manage. */
function SecretRef({ value, configured, visible }: { value: string | null; configured: boolean; visible: boolean }) {
  const { t } = useTranslation('registry');
  if (!configured) return <Badge tone="crit">{t('epp.notConfigured')}</Badge>;
  if (visible && value) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <Badge tone="ok">{t('epp.configured')}</Badge>
        <code className="text-xs text-muted-foreground">{value}</code>
      </span>
    );
  }
  return <Badge tone="ok">{t('epp.configured')}</Badge>;
}

function Environment({ e, refsVisible }: { e: EppConnection; refsVisible: boolean }) {
  const { t } = useTranslation('registry');
  const s = e.settings;
  const activeCred = e.credentials.find((c) => c.status === 'ACTIVE');
  const activeCert = e.certificates.find((c) => c.status === 'ACTIVE');
  const unlimited = t('unlimited');

  const credColumns: Column<EppCredentialView>[] = [
    { key: 'v', header: t('epp.version'), numeric: true, cell: (c) => `v${c.version}` },
    { key: 's', header: t('epp.status'), cell: (c) => <EnumBadge group="credentialStatus" map={CREDENTIAL_STATUS} value={c.status} /> },
    { key: 'client', header: t('epp.clientId'), cell: (c) => (c.client_id ? <code className="text-xs">{c.client_id}</code> : <span className="text-xs text-muted-foreground">{t('epp.hidden')}</span>) },
    { key: 'pw', header: t('epp.password'), cell: (c) => <SecretRef value={c.password_secret_ref} configured={c.password_configured} visible={refsVisible} /> },
    { key: 'act', header: t('epp.activated'), cell: (c) => <span className="text-xs whitespace-nowrap">{formatDateTime(c.activated_at)}</span> },
    { key: 'ret', header: t('epp.retired'), cell: (c) => <span className="text-xs whitespace-nowrap">{formatDateTime(c.retired_at)}</span> },
  ];
  const certColumns: Column<EppCertificateView>[] = [
    { key: 'v', header: t('epp.version'), numeric: true, cell: (c) => `v${c.version}` },
    { key: 's', header: t('epp.status'), cell: (c) => <EnumBadge group="credentialStatus" map={CREDENTIAL_STATUS} value={c.status} /> },
    {
      key: 'valid',
      header: t('epp.valid'),
      cell: (c) => {
        const left = daysUntil(c.not_after);
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5 text-xs whitespace-nowrap">
            {formatDate(c.not_before)} → {formatDate(c.not_after)}
            {c.status === 'ACTIVE' && left < 0 ? <Badge tone="crit">{t('epp.expired')}</Badge> : null}
            {c.status === 'ACTIVE' && left >= 0 && left <= 30 ? <Badge tone="warn">{t('epp.daysLeft', { count: left })}</Badge> : null}
          </span>
        );
      },
    },
    { key: 'key', header: t('epp.privateKey'), cell: (c) => <SecretRef value={c.private_key_secret_ref} configured={c.private_key_configured} visible={refsVisible} /> },
  ];

  const oneRegistry = Boolean(useFixedParam('registry'));
  return (
    <Section
      title={
        <span className="flex flex-wrap items-center gap-2" aria-label={e.registry_name}>
          {oneRegistry ? null : <RegistryRef name={e.registry_name} code={e.registry_code} />}
          {e.active ? <Badge tone="ok">{t('epp.active')}</Badge> : <Badge tone="neutral">{t('epp.inactive')}</Badge>}
        </span>
      }
      description={
        <span className="font-mono text-xs">
          {e.epp_host ?? t('epp.noHost')}:{e.epp_port ?? '—'}
        </span>
      }
    >
      <div className="space-y-5">
        {e.settings_error ? (
          <div role="alert">
            <Callout tone="warn">{t('epp.settingsError')}</Callout>
          </div>
        ) : null}
        {s ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
            <div className="space-y-3">
              <SubHeading>{t('epp.connection')}</SubHeading>
              <KeyValue
                columns={2}
                rows={[
                  [
                    t('epp.tls'),
                    s.tls ? (
                      <span key="t" className="inline-flex flex-wrap items-center gap-1.5">
                        <Badge tone="ok">{t('epp.tlsOn')}</Badge>
                        {s.server_name ? (
                          <span className="text-xs">
                            {t('epp.sni')} <code>{s.server_name}</code>
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <Badge key="t" tone="warn">
                        {t('epp.plainTcp')}
                      </Badge>
                    ),
                  ],
                  [t('epp.privateCa'), <Flag key="ca" on={s.ca_pem_configured} yes={t('epp.configured')} no={t('epp.systemRoots')} />],
                  [t('epp.connectTimeout'), ms(s.connect_timeout_ms)],
                  [t('epp.commandTimeout'), ms(s.command_timeout_ms)],
                  [t('epp.acquireTimeout'), ms(s.acquire_timeout_ms)],
                  [t('epp.breaker'), t('epp.breakerValue', { n: s.failure_threshold, duration: ms(s.open_for_ms) })],
                  [t('epp.maxCommands'), s.max_commands_per_session ?? unlimited],
                  [t('epp.maxIdle'), s.max_session_idle_ms !== null ? ms(s.max_session_idle_ms) : unlimited],
                  [t('epp.maxLifetime'), s.max_session_lifetime_ms !== null ? ms(s.max_session_lifetime_ms) : unlimited],
                ]}
              />
            </div>
            <div className="space-y-3">
              <SubHeading>{t('epp.pools')}</SubHeading>
              <ClassTable settings={s} />
            </div>
          </div>
        ) : null}

        <div className="space-y-3 border-t pt-5">
          <div className="flex items-center gap-2">
            <KeyRound className="size-4 text-muted-foreground" aria-hidden="true" />
            <SubHeading>{t('epp.credentials')}</SubHeading>
          </div>
          {e.credentials.length === 0 ? (
            <Callout tone="warn">{t('epp.noCredentials')}</Callout>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <DataTable columns={credColumns} rows={e.credentials} rowKey={(c) => String(c.version)} />
            </div>
          )}
          {e.credentials.length > 0 && !activeCred ? <p className="text-xs text-warning">{t('epp.noActiveCredential')}</p> : null}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-muted-foreground" aria-hidden="true" />
            <SubHeading>{t('epp.certificates')}</SubHeading>
          </div>
          {e.certificates.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('epp.noCertificates')}</p>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <DataTable columns={certColumns} rows={e.certificates} rowKey={(c) => String(c.version)} />
            </div>
          )}
          {e.certificates.length > 0 && !activeCert ? <p className="text-xs text-warning">{t('epp.noActiveCertificate')}</p> : null}
        </div>
      </div>
    </Section>
  );
}

/** `/registry/epp`: EPP connection configuration per registry environment (read-only; spec §26–§27). */
export function EppPage() {
  const { t } = useTranslation('registry');
  useDocumentTitle(t('epp.title'));
  const crumbs = useRegistryCrumbs();
  const [params, set, reset] = useParamSetter();
  const registry = params.get('registry') ?? '';
  const result = useApi((signal) => api.listEppConnections({ registry: registry || undefined }, { signal }), [registry]);
  return (
    <>
      <PageHeader title={t('epp.title')} crumbs={crumbs} subtitle={t('epp.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      <div className="space-y-6">
        <Callout tone="info" title={t('epp.secretsTitle')}>
          <span className="inline-flex items-start gap-1.5">
            <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{t('epp.secrets')}</span>
          </span>
        </Callout>
        <FilterBar actions={<ResetButton onClick={reset} disabled={!registry} />}>
          <RegistrySelect value={registry} onChange={(v) => set('registry', v)} />
        </FilterBar>
        {result.status === 'loading' ? <Loading rows={3} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('epp.loadError')} /> : null}
        {result.status === 'success' && result.data.items.length === 0 ? <EmptyState title={t('epp.empty')} /> : null}
        {result.status === 'success' ? result.data.items.map((e) => <Environment key={e.id} e={e} refsVisible={result.data.secret_refs_visible} />) : null}
      </div>
    </>
  );
}
